import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { catchError, debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { SurveyVariableConditionFilterDirectionEnum } from 'src/app/enums/survey-variable-condition-filter-direction.enum';
import {
  SurveyVariableConditionFilterUsersScopeTypeEnum,
  SurveyVariableConditionFilterUsersScopeTypeEnumLabels,
  SurveyVariableConditionFilterUsersScopeTypeEnumOptions,
} from 'src/app/enums/survey-variable-condition-filter-users-scope-type.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { SurveyFilter } from 'src/app/models/survey.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';

export type TargetedUserOption = Pick<User, 'id' | 'full_name'> & { label: string };

function formatTargetedUserLabel(user: Pick<User, 'name' | 'surnames' | 'full_name' | 'role'>): string {
  const full_name = user.full_name || `${user.name ?? ''} ${user.surnames ?? ''}`.trim();
  const role_name = user.role?.name?.trim();

  return role_name ? `${full_name} (${role_name})` : full_name;
}

function toTargetedUserOption(user: Pick<User, 'id' | 'name' | 'surnames' | 'full_name' | 'role'>): TargetedUserOption {
  const full_name = user.full_name || `${user.name ?? ''} ${user.surnames ?? ''}`.trim();

  return {
    id: user.id,
    full_name,
    label: formatTargetedUserLabel(user),
  };
}

@Component({
  selector: 'app-targeted-users-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './targeted-users-card.component.html',
  styleUrl: './targeted-users-card.component.css',
  host: { '[attr.title]': 'null' },
  imports: [ReactiveFormsModule, MatRadioModule, MatFormFieldModule, MatInputModule, MatChipsModule, MatAutocompleteModule, MatIconModule],
})
export class TargetedUsersCardComponent {
  private readonly api_service = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly search_input = viewChild<ElementRef<HTMLInputElement>>('search_input');

  readonly title = input('Usuarios');
  readonly description = input<string | null>(null);
  readonly embedded = input(false);
  readonly error_message = input<string | null>(null);
  readonly scope = model.required<SurveyVariableConditionFilterUsersScopeTypeEnum>();
  readonly selection = model.required<TargetedUserOption[]>();
  readonly users_blur = output<void>();

  protected readonly scope_options = SurveyVariableConditionFilterUsersScopeTypeEnumOptions;
  protected readonly scope_labels = SurveyVariableConditionFilterUsersScopeTypeEnumLabels;
  protected readonly manual_scope = SurveyVariableConditionFilterUsersScopeTypeEnum.Manual;
  protected readonly search_control = new FormControl('', { nonNullable: true });
  protected readonly suggestions = signal<TargetedUserOption[]>([]);
  protected readonly is_manual = computed(() => this.scope() === this.manual_scope);
  protected readonly selected_ids_set = computed(() => new Set(this.selection().map(user => user.id)));
  protected readonly display_search_value = (value: string | TargetedUserOption | null): string => (typeof value === 'string' ? value : '');

  constructor() {
    this.search_control.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap(raw_term => {
          const term = typeof raw_term === 'string' ? raw_term.trim() : '';

          if (term.length < 2) {
            return of([] as TargetedUserOption[]);
          }

          return this.api_service.getUsers({ search: term, limit: 20, with: 'role' }).pipe(
            map((response: ArrayResponse<User>): TargetedUserOption[] => {
              const selected_ids = this.selected_ids_set();

              return (response.data ?? []).filter(user => !selected_ids.has(user.id)).map(toTargetedUserOption);
            }),
            catchError(() => of([] as TargetedUserOption[]))
          );
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(users => {
        this.suggestions.set(users);
      });
  }

  protected onScopeChange(scope: SurveyVariableConditionFilterUsersScopeTypeEnum): void {
    this.scope.set(scope);

    if (scope !== this.manual_scope) {
      this.selection.set([]);
      this.search_control.setValue('');
      this.suggestions.set([]);

      return;
    }

    afterNextRender(() => this.search_input()?.nativeElement.focus(), { injector: this.injector });
  }

  protected onUserSelected(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value as TargetedUserOption;

    if (!this.selected_ids_set().has(user.id)) {
      this.selection.set([...this.selection(), user]);
    }

    this.suggestions.set([]);
  }

  protected onSearchPanelClosed(): void {
    this.search_control.setValue('');
    const input = this.search_input()?.nativeElement;
    if (input) {
      input.value = '';
    }
  }

  protected removeUser(user_id: number): void {
    this.selection.set(this.selection().filter(user => user.id !== user_id));
  }

  protected onUsersInputBlur(event: FocusEvent): void {
    const next_focus = event.relatedTarget;

    if (next_focus instanceof HTMLElement && next_focus.closest('.mat-mdc-autocomplete-panel, .mat-autocomplete-panel')) {
      return;
    }

    this.users_blur.emit();
  }

  protected displayUserLabel(user: TargetedUserOption): string {
    return user.label;
  }
}

export function getToAskAboutUsersScope(filters: SurveyFilter[]): SurveyVariableConditionFilterUsersScopeTypeEnum {
  const filter = filters.find(item => item.direction === SurveyVariableConditionFilterDirectionEnum.ToAskAbout);

  return filter?.users_scope_type ?? SurveyVariableConditionFilterUsersScopeTypeEnum.None;
}

export function getToAskAboutUsers(filters: SurveyFilter[]): TargetedUserOption[] {
  const filter = filters.find(item => item.direction === SurveyVariableConditionFilterDirectionEnum.ToAskAbout);

  if (filter?.users_scope_type !== SurveyVariableConditionFilterUsersScopeTypeEnum.Manual) {
    return [];
  }

  return (filter.users ?? []).map(toTargetedUserOption);
}

export function setTargetedUserSelection(
  scope_control: FormControl<SurveyVariableConditionFilterUsersScopeTypeEnum>,
  users_control: FormControl<TargetedUserOption[]>,
  scope: SurveyVariableConditionFilterUsersScopeTypeEnum,
  users: TargetedUserOption[]
): void {
  scope_control.setValue(scope);
  users_control.setValue(scope === SurveyVariableConditionFilterUsersScopeTypeEnum.Manual ? users : []);
  scope_control.markAsDirty();
  scope_control.markAsTouched();
  users_control.markAsDirty();
  users_control.markAsTouched();
}
